import React, { useState, useEffect } from 'react';
import {
  ActiveRollInfo,
  RegionalRollRecord,
  getRegionalRolls,
  getCompletedSetsCount
} from '../lib/gameEngine';
import './OrderRollsMenu.css';

interface OrderRollsMenuProps {
  dailyFreeRollsLeft: number;
  wallet: number;
  activeRolls: Record<string, ActiveRollInfo>;
  onBack: () => void;
  onBuyStandardRoll: () => void;
  onBuyRoll?: (rollType: string) => void;
  loading?: boolean;
}

const getFeeLabel = (cost: number, faceValue: number): string => {
  if (faceValue <= 0 || cost <= faceValue) return '(0% fee)';
  const feePercent = ((cost - faceValue) / faceValue) * 100;
  const rounded = Math.round(feePercent * 10) / 10;
  return `(${rounded}% fee)`;
};

export const OrderRollsMenu: React.FC<OrderRollsMenuProps> = ({
  dailyFreeRollsLeft,
  wallet,
  activeRolls,
  onBack,
  onBuyStandardRoll,
  onBuyRoll,
  loading = false
}) => {
  const [activeTab, setActiveTab] = useState<'standard' | 'regional'>('standard');
  const [regionalRolls, setRegionalRolls] = useState<RegionalRollRecord[]>([]);
  const [completedSets, setCompletedSets] = useState<number>(0);
  const [fetchingRegionals, setFetchingRegionals] = useState<boolean>(false);

  const standardRollActive = activeRolls['standard_2euro'];
  const isInProgress = Boolean(standardRollActive);

  const standardRollCost = dailyFreeRollsLeft > 0 ? 50.00 : 52.50;
  const standardFeeLabel = getFeeLabel(standardRollCost, 50.00);
  const canAffordStandard = isInProgress || wallet >= standardRollCost;

  // Fetch regional rolls and user album stats for set unlocking
  useEffect(() => {
    let mounted = true;
    const loadRegionalData = async () => {
      setFetchingRegionals(true);
      try {
        const [rolls, count] = await Promise.all([
          getRegionalRolls(),
          getCompletedSetsCount()
        ]);
        if (mounted) {
          setRegionalRolls(rolls);
          setCompletedSets(count);
        }
      } catch (err) {
        console.error('Error loading regional rolls:', err);
      } finally {
        if (mounted) setFetchingRegionals(false);
      }
    };

    loadRegionalData();
    return () => {
      mounted = false;
    };
  }, [activeTab]);

  const handleBuy = (rollType: string) => {
    if (onBuyRoll) {
      onBuyRoll(rollType);
    } else if (rollType === 'standard_2euro') {
      onBuyStandardRoll();
    }
  };

  // Spacebar triggers buy or resume on the standard tab
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && activeTab === 'standard' && !loading && canAffordStandard) {
        e.preventDefault();
        handleBuy('standard_2euro');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, loading, canAffordStandard]);

  return (
    <div className="order-rolls-container">
      {/* Navigation */}
      <div className="order-rolls-nav">
        <button className="back-btn" onClick={onBack}>
          ← Back to Main Menu
        </button>
      </div>

      <div className="order-rolls-header">
        <h2 className="order-rolls-title">Order Coin Rolls</h2>
        <div className="order-rolls-subtitle">
          Select a roll to order and start inspecting
        </div>
      </div>

      {/* Tabs */}
      <div className="order-tabs-nav">
        <button
          className={`tab-btn ${activeTab === 'standard' ? 'active' : ''}`}
          onClick={() => setActiveTab('standard')}
        >
          Standard Rolls
        </button>
        <button
          className={`tab-btn ${activeTab === 'regional' ? 'active' : ''}`}
          onClick={() => setActiveTab('regional')}
        >
          Regional Rolls
        </button>
      </div>

      {activeTab === 'standard' ? (
        <div className="rolls-grid">
              {/* Standard Denomination Rolls */}
              {[
                { id: 'standard_2euro', name: '2€ Standard Roll', coins: 25, faceValue: 50.00, cost: standardRollCost, feeLabel: standardFeeLabel, hasFreeRolls: true },
                { id: 'standard_1euro', name: '1€ Standard Roll', coins: 25, faceValue: 25.00, cost: 26.25, feeLabel: getFeeLabel(26.25, 25.00), hasFreeRolls: false },
                { id: 'standard_50cent', name: '50¢ Standard Roll', coins: 40, faceValue: 20.00, cost: 21.00, feeLabel: getFeeLabel(21.00, 20.00), hasFreeRolls: false },
                { id: 'standard_20cent', name: '20¢ Standard Roll', coins: 40, faceValue: 8.00, cost: 8.40, feeLabel: getFeeLabel(8.40, 8.00), hasFreeRolls: false },
                { id: 'standard_10cent', name: '10¢ Standard Roll', coins: 40, faceValue: 4.00, cost: 4.20, feeLabel: getFeeLabel(4.20, 4.00), hasFreeRolls: false },
                { id: 'standard_5cent', name: '5¢ Standard Roll', coins: 50, faceValue: 2.50, cost: 2.63, feeLabel: getFeeLabel(2.63, 2.50), hasFreeRolls: false },
                { id: 'standard_2cent', name: '2¢ Standard Roll', coins: 50, faceValue: 1.00, cost: 1.05, feeLabel: getFeeLabel(1.05, 1.00), hasFreeRolls: false },
                { id: 'standard_1cent', name: '1¢ Standard Roll', coins: 50, faceValue: 0.50, cost: 0.53, feeLabel: getFeeLabel(0.53, 0.50), hasFreeRolls: false }
              ].map((roll) => {
                const activeRoll = activeRolls[roll.id];
                const isRollActive = Boolean(activeRoll);
                const canAfford = isRollActive || wallet >= roll.cost;

                return (
                  <div
                    key={roll.id}
                    className={`roll-card active-card roll-theme-${roll.id} ${isRollActive ? 'in-progress-card' : ''}`}
                  >
                    <div className="roll-card-top">
                      <div className="roll-card-title">{roll.name}</div>
                      <div className="roll-card-stats">
                        <div className="stat-line">
                          <span>Coins:</span>
                          <strong>{roll.coins} coins</strong>
                        </div>
                        <div className="stat-line">
                          <span>Face Value:</span>
                          <strong>€{roll.faceValue.toFixed(2)}</strong>
                        </div>
                        {roll.hasFreeRolls && (
                          <div className="stat-line">
                            <span>Daily No-Fee Rolls:</span>
                            <strong>{dailyFreeRollsLeft} / 3 left</strong>
                          </div>
                        )}
                        <div className="stat-line cost-line">
                          <span>Cost:</span>
                          {isRollActive ? (
                            <strong className="cost-strikethrough">
                              €{roll.cost.toFixed(2)} {roll.feeLabel}
                            </strong>
                          ) : (
                            <strong>
                              €{roll.cost.toFixed(2)} {roll.feeLabel}
                            </strong>
                          )}
                        </div>
                      </div>

                      {isRollActive && (
                        <div className="in-progress-block">
                          <div className="in-progress-label">In progress!</div>
                          <div className="in-progress-sub">
                            ({activeRoll.remainingCoins} coins remaining)
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      className={`roll-buy-btn ${isRollActive ? 'in-progress-btn' : ''}`}
                      onClick={() => handleBuy(roll.id)}
                      disabled={loading || (!isRollActive && !canAfford)}
                    >
                      {loading
                        ? 'Loading...'
                        : isRollActive
                        ? 'FINISH HUNT (Free)'
                        : !canAfford
                        ? 'Insufficient Funds'
                        : 'BUY & HUNT'}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <div className="tab-info-text">
                Regional rolls heavily bias coin finds towards native issues and ones from nearby countries.
              </div>

              <div className="rolls-grid">
                {fetchingRegionals && regionalRolls.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
                    Loading regional rolls from bank...
                  </div>
                ) : (
                  regionalRolls.map(regional => {
                    const activeRoll = activeRolls[regional.id];
                    const isRollActive = Boolean(activeRoll);
                    const isUnlocked = completedSets >= regional.required_completed_sets;
                    const costNumber = Number(regional.cost);
                    const canAfford = isRollActive || wallet >= costNumber;
                    const faceValue = regional.denomination === '2€' || regional.denomination.includes('2€')
                      ? regional.coin_count * 2
                      : regional.coin_count * 1;
                    const feeLabel = getFeeLabel(costNumber, faceValue);

                    return (
                      <div
                        key={regional.id}
                        className={`roll-card ${
                          !isUnlocked
                            ? 'locked-card'
                            : isRollActive
                            ? 'active-card in-progress-card'
                            : 'active-card'
                        }`}
                      >
                        <div className="roll-card-top">
                          <div className="roll-card-title">
                            {regional.flag_emoji} {regional.name} - {regional.denomination} Roll
                          </div>
                          <div className="roll-card-stats">
                            <div className="stat-line">
                              <span>Coins:</span>
                              <strong>{regional.coin_count} coins</strong>
                            </div>
                            <div className="stat-line">
                              <span>Face Value:</span>
                              <strong>€{faceValue.toFixed(2)}</strong>
                            </div>
                            <div className="stat-line cost-line">
                              <span>Cost:</span>
                              {isRollActive ? (
                                <strong className="cost-strikethrough">
                                  €{costNumber.toFixed(2)} {feeLabel}
                                </strong>
                              ) : (
                                <strong>€{costNumber.toFixed(2)} {feeLabel}</strong>
                              )}
                            </div>

                            {!isUnlocked && (
                              <div className="lock-badge">
                                🔒 Complete {regional.required_completed_sets} Circulation Set{regional.required_completed_sets > 1 ? 's' : ''} to Unlock! ({completedSets}/{regional.required_completed_sets} Sets)
                              </div>
                            )}
                          </div>

                          {isRollActive && (
                            <div className="in-progress-block">
                              <div className="in-progress-label">In progress!</div>
                              <div className="in-progress-sub">
                                ({activeRoll.remainingCoins} coins remaining)
                              </div>
                            </div>
                          )}
                        </div>

                        <button
                          className={`roll-buy-btn ${isRollActive ? 'in-progress-btn' : ''}`}
                          disabled={loading || !isUnlocked || (!isRollActive && !canAfford)}
                          onClick={() => handleBuy(regional.id)}
                        >
                          {loading
                            ? 'Loading...'
                            : !isUnlocked
                            ? 'LOCKED'
                            : isRollActive
                            ? 'FINISH HUNT (Free)'
                            : !canAfford
                            ? 'Insufficient Funds'
                            : 'BUY & HUNT'}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
    </div>
  );
};

